/**
 * EndPoints.ts
 *
 * Centralized collection of API endpoint URLs used across the application.
 *
 * Each endpoint is grouped by functional domain for easier maintainability.
 * The base API URL and Socket URL are imported from CommonConstants.
 */

import { BASE_APIS_URL, SOCKET_BASE_URL } from "../constants/CommonConstants";

export const EndPoints = {
  // ----------- Geographical Data -----------
  areas: `${BASE_APIS_URL}/Areas`,
  cities: `${BASE_APIS_URL}/Cities`,
  countries: `${BASE_APIS_URL}/Countries`,
  facilityTypes: `${BASE_APIS_URL}/FacilityTypes`,
  contactTypes: `${BASE_APIS_URL}/ContactTypes`,

  // ----------- Authentication -----------
  checkFromNumber: `${BASE_APIS_URL}/Auth/CheckFromNumber`,
  createPassword: `${BASE_APIS_URL}/Auth/CreatePassword`,
  forgetPasswordResendCode: `${BASE_APIS_URL}/Auth/ForgetPassword/ResendCode`,
  forgetPasswordSendCode: `${BASE_APIS_URL}/Auth/ForgetPassword/SendCode`,
  ForgetPasswordVerifyOTP: `${BASE_APIS_URL}/Auth/ForgetPassword/CheckCode`,
  profileForgetPasswordResetPassword: `${BASE_APIS_URL}/Auth/ForgetPasword/ResetPassword`,
  ChangePhoneNumberResendCode: `${BASE_APIS_URL}/Accounts/ChangePhoneNumber/Resend`,
  resendCode: `${BASE_APIS_URL}/Auth/ResendCode`,
  userLogin: `${BASE_APIS_URL}/Auth/Login`,
  userRegister: `${BASE_APIS_URL}/Auth/Register`,
  verifyOTP: `${BASE_APIS_URL}/Auth/VerifyNumber`,
  completeStaff: `${BASE_APIS_URL}/Auth/CompleteStaff`,

  // ----------- Accounts & Profile -----------
  changeLanguage: `${BASE_APIS_URL}/Accounts/ChangeLanguage`,
  changePassword: `${BASE_APIS_URL}/Accounts/ChangePassword`,
  checkAccountPassword: `${BASE_APIS_URL}/Accounts/ChangePhoneNumber/CheckFromPassword`,
  deleteMyAccount: `${BASE_APIS_URL}/Accounts/DeleteAccount`,
  logout: `${BASE_APIS_URL}/Accounts/Logout`,
  profile: `${BASE_APIS_URL}/Accounts/profile`,
  profileChangePhoneNumberVerify: `${BASE_APIS_URL}/Accounts/ChangePhoneNumber/Verify`,
  profileCheckFromPhoneNumber: `${BASE_APIS_URL}/Accounts/ChangePhoneNumber/CheckfromPhoneNumber`,
  updateProfileImage: `${BASE_APIS_URL}/Accounts/UpdateImage`,
  updateProfilePersonalInfo: `${BASE_APIS_URL}/Accounts/UpdatePersonalInfo`,
  switchAccount: `${BASE_APIS_URL}/Accounts/SwitchAccount`,
  settingsNotifications: `${BASE_APIS_URL}/Accounts/NotificationSettings`,
  updateSettingsNotifications: `${BASE_APIS_URL}/Accounts/NotificationSettings`,
  getProfileContactTypes: `${BASE_APIS_URL}/Facilities/ContactsInfo`,
  UpdateStaffLicense: `${BASE_APIS_URL}/Accounts/UpdateStaffDocument`,
  // ----------- Facilities -----------
  addNewStaff: `${BASE_APIS_URL}/Facilities/Staff/AddNewStaff`,
  createFacility: `${BASE_APIS_URL}/facilities/create`,
  deleteStaff: `${BASE_APIS_URL}/Facilities/Staff/Delete`,
  facilities: `${BASE_APIS_URL}/Facilities`,
  facilityContactTypes: `${BASE_APIS_URL}/Facilities/ContactsInfo`,
  facilityUpdateContactInfo: `${BASE_APIS_URL}/Facilities/UpdateContactInfo`,
  facilityUpdateInsurance: `${BASE_APIS_URL}/Facilities/UpdateInsurance`,
  getFacilitiesSchedules: `${BASE_APIS_URL}/Facilities/Schedules`,
  getFacilityLicense: `${BASE_APIS_URL}/Facilities/License`,
  getManufacturers: `${BASE_APIS_URL}/Facilities/Manufacturers`,
  getProfileComapnies: `${BASE_APIS_URL}/Facilities/Companies`,
  getStaff: `${BASE_APIS_URL}/Facilities/Staff`,
  getWarehoueses: `${BASE_APIS_URL}/Facilities/Warhouses`,
  getPharmacies: `${BASE_APIS_URL}/Facilities/Pharmacies`,
  searchStaff: `${BASE_APIS_URL}/Facilities/Staff/Search`,
  updateFacilityAddress: `${BASE_APIS_URL}/Facilities/UpdateAddress`,
  updateFacilityProfile: `${BASE_APIS_URL}/Facilities/Update`,
  updateFacilitiesSchedule: `${BASE_APIS_URL}/Facilities/UpdateSchedule`,
  updateProfileFacilityImage: `${BASE_APIS_URL}/Facilities/UpdateImage`,
  getWarehouseComapnies: `${BASE_APIS_URL}/Facilities/Companies`,

  // ----------- Drugs -----------
  changeDrugStatus: `${BASE_APIS_URL}/Drugs/ChangeStatus`,
  deleteMineDrugById: `${BASE_APIS_URL}/Drugs/Delete`,
  drugAlternatives: `${BASE_APIS_URL}/Drugs/Similar`,
  drugAvailableIn: `${BASE_APIS_URL}/Drugs/AvailableIn`,
  drugBestOffers: `${BASE_APIS_URL}/Drugs/BestOffers`,
  getDrugs: `${BASE_APIS_URL}/Drugs`,
  getMineDrugs: `${BASE_APIS_URL}/Drugs/Mine`,
  importDrugsExcel: `${BASE_APIS_URL}/Drugs/ImportExcel`,
  linkDrugs: `${BASE_APIS_URL}/Drugs/Link`,

  // ----------- Offers -----------
  createOffer: `${BASE_APIS_URL}/Offers`,
  deleteOfferById: `${BASE_APIS_URL}/Offers/Delete`,
  endOffer: `${BASE_APIS_URL}/Offers/Close`,
  getOfferById: `${BASE_APIS_URL}/Offers`,
  getOfferItems: `${BASE_APIS_URL}/Offers/Items`,
  getOffers: `${BASE_APIS_URL}/Offers`,
  pharamcyOffers: `${BASE_APIS_URL}/Offers`,
  updateOffers: `${BASE_APIS_URL}/Offers/Update`,

  // ----------- Orders -----------
  createOrder: `${BASE_APIS_URL}/Orders`,
  deleteOrders: `${BASE_APIS_URL}/Orders/Delete`,
  deletePatientOrders: `${BASE_APIS_URL}/PatientOrders/Delete`,
  downloadOrderExcel: `${BASE_APIS_URL}/Orders/Download/Excel`,
  exportOrderPdf: `${BASE_APIS_URL}/Orders/Download/PDF`,
  getOrderExportTokenLicense: `${BASE_APIS_URL}/Orders/ExportTokenLicense`,
  getOrders: `${BASE_APIS_URL}/Orders`,
  orderExportById: `${BASE_APIS_URL}/Orders/Export`,
  patientResponseOrder: `${BASE_APIS_URL}/PatientOrders/Response`,
  responseOrder: `${BASE_APIS_URL}/Orders/Response`,
  toggleIsReadyOrder: `${BASE_APIS_URL}/Orders/Ready`,
  getPatientOrders: `${BASE_APIS_URL}/PatientOrders`,
  addOrderDiscount: `${BASE_APIS_URL}/Orders/Discount`,
  // ----------- Products -----------
  changeProductStatus: `${BASE_APIS_URL}/Products/ChangeStatus`,
  createProduct: `${BASE_APIS_URL}/Products`,
  deleteProductById: `${BASE_APIS_URL}/Products/Delete`,
  getProducts: `${BASE_APIS_URL}/Products`,
  importProductsExcel: `${BASE_APIS_URL}/Products/ImportExcel`,
  updateProduct: `${BASE_APIS_URL}/Products/Update`,

  // ----------- Resources & Search -----------
  getAds: `${BASE_APIS_URL}/Ads`,
  getSearch: `${BASE_APIS_URL}/search`,
  resources: `${BASE_APIS_URL}/Resources`,

  // ----------- Settings & Management -----------
  settingsManagementFAQ: `${BASE_APIS_URL}/Management/FAQ`,
  settingsManagementManagement: `${BASE_APIS_URL}/Management/Management`,

  // ----------- Notifications & Sockets -----------
  getHomeNotifications: `${BASE_APIS_URL}/Notifications/Home`,
  getNotifications: `${BASE_APIS_URL}/Notifications`,
  NotificationsSocketURL: `${SOCKET_BASE_URL}/NotificationsHub?Authorization=`,
  socketURL: `${SOCKET_BASE_URL}/OrdersHub?Authorization=`,

  // ----------- Miscellaneous -----------
  getUpdateOrderReqeustPrice: `${BASE_APIS_URL}/Orders/Price`,

  // ----------- Share Cart -----------
  AddShareCartToFacility: `${BASE_APIS_URL}/Orders/AddShareCartToFacility`,
  getSharedCartById: `${BASE_APIS_URL}/Orders/GetSharedCart`,
  getSharedCartOptions: `${BASE_APIS_URL}/Orders/GetApplySharedCartOptions`,
  applySharedCart: `${BASE_APIS_URL}/Orders/ApplySharedCart`,
  // ----------- Points  Offer -----------
  GetMyPointsWallet: `${BASE_APIS_URL}/Facilities/GetMyPointsWallet`,
  GetMinimumProfit: `${BASE_APIS_URL}/Orders/GetMinimumProfit`,
  GetMyPointsLog: `${BASE_APIS_URL}/PointsOffers/GetMyPointsLog`,
  PointsOffer: `${BASE_APIS_URL}/PointsOffers`,
  PurchasePointsOffer: `${BASE_APIS_URL}/PointsOffers/PurchasePointsOffer`,

};
